import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ArrowLeft, BookOpen, FileText, Link as LinkIcon, Filter, Paperclip, Image as ImageIcon, FileType } from "lucide-react";

const fileIconFor = (ct = "", name = "") => {
    if (ct.startsWith("image/")) return <ImageIcon className="w-4 h-4" />;
    if (ct === "application/pdf" || name.toLowerCase().endsWith(".pdf")) return <FileText className="w-4 h-4" />;
    return <FileType className="w-4 h-4" />;
};

const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function StudentLMS() {
    const [posts, setPosts] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [me, setMe] = useState(null);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [m, s] = await Promise.all([api.get("/student/me"), api.get("/subjects")]);
                setMe(m.data); setSubjects(s.data);
                const tt = await api.get("/student/timetable");
                const r = await api.get("/lms/posts", { params: { class_id: tt.data.class_id || undefined } });
                setPosts(r.data);
            } finally { setLoading(false); }
        })();
    }, []);

    const filtered = useMemo(() => filter === "all" ? posts : posts.filter((p) => p.subject_id === filter), [posts, filter]);

    // Group by section within the filtered list. Posts without a section fall under "Pa seksion".
    const bySection = useMemo(() => {
        const map = new Map();
        for (const p of filtered) {
            const key = (p.section && p.section.trim()) || "Pa seksion";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(p);
        }
        return Array.from(map.entries());
    }, [filtered]);

    return (
        <Layout>
            <Link to="/student" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Materialet</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">LMS — Burimet mësimore</h1>
                <p className="text-slate-600 mt-2 text-sm">Materiale të publikuara nga mësuesit</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 font-bold">
                    <Filter className="w-4 h-4" /> Filtro
                </div>
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2 font-semibold flex-1 sm:flex-none" data-testid="lms-filter-subject">
                    <option value="all">Të gjitha lëndët</option>
                    {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div className="ml-auto text-xs text-slate-500"><span className="font-bold text-oxford">{filtered.length}</span> nga {posts.length}</div>
            </div>

            {loading ? <div className="text-slate-500">Duke ngarkuar…</div> : filtered.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <BookOpen className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">Pa materiale ende</div>
                    <p className="text-sm text-slate-600 mt-1">Mësuesit nuk kanë publikuar gjë në këtë filtër.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {bySection.map(([section, items]) => (
                        <div key={section} data-testid={`lms-section-${section.replace(/\s+/g, "_")}`}>
                            <div className="flex items-center gap-2 mb-3 sticky top-16 bg-paper/95 backdrop-blur-sm py-1 z-10">
                                <div className="h-px flex-1 bg-slate-200"></div>
                                <h2 className="font-heading text-sm font-bold text-oxford uppercase tracking-widest">{section}</h2>
                                <span className="text-xs text-slate-500">{items.length}</span>
                                <div className="h-px flex-1 bg-slate-200"></div>
                            </div>
                            <div className="space-y-3">
                                {items.map((p) => {
                                    const hasAtt = (p.attachments?.length || 0) > 0;
                                    const Icon = hasAtt ? Paperclip : (p.kind === "link" ? LinkIcon : p.kind === "file" ? FileText : BookOpen);
                                    return (
                                        <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex gap-4" data-testid={`lms-post-${p.id}`}>
                                            <div className="w-12 h-12 rounded-xl bg-sage/10 text-sage flex items-center justify-center shrink-0"><Icon className="w-6 h-6" /></div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                    <span className="font-heading font-bold text-slate-900">{p.title}</span>
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-oxford/10 text-oxford text-[11px] font-bold">{p.subject_name}</span>
                                                </div>
                                                <div className="text-xs text-slate-500 mb-2">{p.teacher_name} • {p.created_at?.slice(0, 10)}</div>
                                                {p.body && <p className="text-slate-700 text-sm leading-relaxed mb-2 whitespace-pre-wrap">{p.body}</p>}
                                                {p.url && (
                                                    <a href={p.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-oxford font-semibold text-sm hover:underline" data-testid={`lms-link-${p.id}`}>
                                                        <LinkIcon className="w-4 h-4" /> Hap burimin
                                                    </a>
                                                )}
                                                {hasAtt && (
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {p.attachments.map((a) => (
                                                            <a key={a.file_id} href={`${api.defaults.baseURL}/lms/file/${a.file_id}?auth=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer"
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sage/10 text-sage hover:bg-sage/20 text-xs font-semibold border border-sage/30 transition"
                                                                data-testid={`lms-att-${a.file_id}`}>
                                                                {fileIconFor(a.content_type, a.filename)} {a.filename} <span className="text-[10px] text-slate-500">({fmtSize(a.size)})</span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Layout>
    );
}
